import type { Folha } from '../world/spriteAtlas';

/**
 * As cores do guarda-roupa (techspec-guardaroupa). As folhas das peças são cinza, nas cores da UO; a matiz é
 * aplicada como o cliente faz com matiz cheia: cada pixel vira o tom `vermelho * 31 / 255` (0–31) dos 32 tons da
 * matiz, lidos de `public/cores/traje.json` (gerado do hues.mul por `tools/traje-arte.py`). Matiz 0 é Natural.
 */

type Tons = readonly (readonly [number, number, number])[];

let tabela: Record<string, Tons> | null = null;
let aquecimento: Promise<void> | null = null;
const aoCarregar = new Set<() => void>();

export function aquecerTraje(): Promise<void> {
  if (tabela) return Promise.resolve();
  aquecimento ??= fetch('/cores/traje.json')
    .then(r => (r.ok ? r.json() : {}))
    .then((j: { matizes?: Record<string, Tons> }) => { tabela = j?.matizes && typeof j.matizes === 'object' ? j.matizes : {}; })
    .catch(() => { tabela = {}; })
    .finally(() => { for (const f of aoCarregar) f(); aoCarregar.clear(); });
  return aquecimento;
}

/** Os 32 tons da matiz, ou null (Natural, matiz desconhecida, tabela ainda não carregada). */
export function tonsDaMatiz(hue: number): Tons | null {
  if (!hue) return null;
  if (!tabela) { void aquecerTraje(); return null; }
  const t = tabela[String(hue)];
  return t && t.length === 32 ? t : null;
}

/** Repinta quando a tabela chegar (o motor chama paintDolls). */
export function quandoTrajeCarregar(f: () => void): void {
  if (tabela) return;
  aoCarregar.add(f);
  void aquecerTraje();
}

/** Cor CSS de uma matiz para bolinhas e chips: um tom claro do meio da rampa. Natural é o cinza da arte. */
export function corDaMatiz(hue: number): string {
  const t = tonsDaMatiz(hue);
  if (!t) return 'rgb(154 154 154)';
  const [r, g, b] = t[22];
  return `rgb(${r} ${g} ${b})`;
}

/** Degradê da matiz inteira, para a joia da cor prime. */
export function degradeDaMatiz(hue: number): string {
  const t = tonsDaMatiz(hue);
  if (!t) return 'linear-gradient(135deg, #bbb, #777)';
  const c = (i: number) => `rgb(${t[i][0]} ${t[i][1]} ${t[i][2]})`;
  return `linear-gradient(135deg, ${c(30)}, ${c(20)} 45%, ${c(9)})`;
}

/**
 * As peças de matiz parcial, como a UO faz com a matiz de bit 0x8000: só os pixels cinza (o pano) pegam a cor; pele,
 * dourado e o resto colorido da arte ficam como estão. O Manto do GM tem mãos, pés e barra dourada na própria arte
 * (Renato, 23/10: a pintura não muda a cor do personagem). Vale também para o Manto da Morte, que é a mesma família.
 */
const MATIZ_PARCIAL: ReadonlySet<string> = new Set(['manto-gm', 'manto-morte']);

/**
 * A peça que usa a folha de outra: o Shroud Robe (techspec-pentagrama §07) é o hooded shroud da UO, que anima com a
 * mesma arte do Manto da Morte; muda só a matiz fixa (1158), que vem no fio do servidor.
 */
export const ARTE_DA_PECA: Readonly<Record<string, string>> = { shroud: 'manto-morte' };

/** A peça do traje pela url da folha ou do ícone (`…/traje/manto-gm/idle/…`, `…/itens/traje/manto-gm.png`). */
function pecaDaUrl(url: string): string {
  const m = /\/traje\/([a-z0-9-]+)(?:\/|\.png)/.exec(url);
  return m ? m[1] : '';
}

export function matizParcial(url: string): boolean {
  return MATIZ_PARCIAL.has(pecaDaUrl(url));
}

/** Tinge os pixels de um canvas (in place). `parcial`: só os cinza (r = g = b). */
export function tingirPixels(d: Uint8ClampedArray, tons: Tons, parcial = false): void {
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    if (parcial && (Math.abs(d[i] - d[i + 1]) > 2 || Math.abs(d[i + 1] - d[i + 2]) > 2)) continue;
    const tom = tons[Math.min(31, Math.round(d[i] * 31 / 255))];
    d[i] = tom[0];
    d[i + 1] = tom[1];
    d[i + 2] = tom[2];
  }
}

/** A imagem (folha ou ícone) tingida num canvas novo. Sem tons, devolve a fonte. */
export function tingirFonteTraje(src: CanvasImageSource, w: number, h: number, hue: number, parcial = false): CanvasImageSource {
  const tons = tonsDaMatiz(hue);
  if (!tons) return src;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const g = c.getContext('2d', { willReadFrequently: true });
  if (!g) return src;
  g.imageSmoothingEnabled = false;
  g.drawImage(src, 0, 0);
  const img = g.getImageData(0, 0, w, h);
  tingirPixels(img.data, tons, parcial);
  g.putImageData(img, 0, 0);
  return c;
}

const TINGIDAS_MAX = 96;
const tingidas = new Map<string, Folha>();

/** A folha da peça na matiz. Natural (0) e tabela ausente devolvem a folha cinza. */
export function tingirFolhaTraje(folha: Folha, hue: number): Folha {
  if (!hue || !folha.url.includes('/traje/')) return folha;
  if (!tonsDaMatiz(hue)) return folha;
  const key = `${folha.url}#traje${hue}#${folha.frames}`;
  const hit = tingidas.get(key);
  if (hit) {
    tingidas.delete(key);
    tingidas.set(key, hit);
    return hit.fps === folha.fps ? hit : { ...hit, fps: folha.fps };
  }
  let w = 0;
  let h = 0;
  for (const linha of folha.dirs) {
    for (const q of linha) {
      w = Math.max(w, q.sx + q.w);
      h = Math.max(h, q.sy + q.h);
    }
  }
  const fonte = tingirFonteTraje(folha.fonte, w, h, hue, matizParcial(folha.url));
  const next: Folha = { ...folha, url: key, fonte, dirs: folha.dirs.map(row => row.map(q => ({ ...q, fonte }))) };
  tingidas.set(key, next);
  while (tingidas.size > TINGIDAS_MAX) {
    const velha = tingidas.keys().next();
    if (velha.done) break;
    tingidas.delete(velha.value);
  }
  return next;
}

/**
 * A ordem de desenho do UO (ClassicUO, olhando para a câmera), por peça. As placas entram nos mesmos números
 * (pernas 50, peito 60, braços 70, luvas 90, elmo 160; o robe de NPC 110). A capa é a única que muda com a
 * direção: atrás do corpo de frente, por cima de lado e de costas.
 */
export const ORDEM_DO_TRAJE: Readonly<Record<string, number>> = {
  camisa: 20, 'camisa-fina': 20,
  calca: 30, 'calca-curta': 30,
  sapatos: 40, sandalias: 40, botas: 40, 'botas-altas': 40,
  gibao: 80, tunica: 80, sobretudo: 80,
  faixa: 85,
  kilt: 100, saia: 100,
  manto: 110, vestido: 110, 'vestido-fino': 110,
  avental: 120,
  capa: 140,
  'chapeu-mago': 160, 'chapeu-pena': 160, tricornio: 160, 'chapeu-aba-larga': 160, 'chapeu-palha': 160,
  bandana: 160, gorro: 160,
  // As skins de Natal (techspec-skins): logo ACIMA da peça de placa que elas cobrem (Renato, 28/10/2026: com a
  // armadura visível, a skin fica por cima dela). O gorro some com o elmo à mostra, como o chapéu.
  'natal-calcas': 52, 'natal-botas': 54, 'natal-casaco': 62, 'natal-corpete': 62, 'natal-mangas': 72,
  'natal-luvas': 92, 'natal-saia': 100, 'natal-gorro': 160,
  // Os mantos tingíveis (Manto do GM, Manto da Morte): no lugar do manto.
  'manto-gm': 110, 'manto-morte': 110, shroud: 110,
  'manto-fundador-peregrino': 110, 'manto-fundador-cavaleiro': 110, 'manto-fundador-lorde': 110,
};

/**
 * Os mantos com capuz (o Manto do GM e o Manto da Morte): o capuz cobre a cabeça, então cabelo, chapéu e elmo não são
 * pintados por cima dele. É o que o ClassicUO faz com o GM Robe e o death shroud; a barba continua aparecendo.
 */
export const MANTOS_COM_CAPUZ: ReadonlySet<string> = new Set([
  'manto-gm', 'manto-morte', 'shroud', 'manto-fundador-peregrino', 'manto-fundador-cavaleiro', 'manto-fundador-lorde'
]);

export const CAPA = 'capa';
export const ORDEM_CABECA = 160;

const icones = new Map<string, Promise<string>>();

/** O ícone da peça ou da tinta na matiz, como data URL (cacheado). Sem tons, a arte cinza. */
export function iconeDoTraje(src: string, hue: number): Promise<string> {
  if (!hue) return Promise.resolve(src);
  const key = `${src}#${hue}`;
  let p = icones.get(key);
  if (!p) {
    p = aquecerTraje().then(() => new Promise<string>(resolve => {
      const img = new Image();
      img.onload = () => {
        const c = tingirFonteTraje(img, img.naturalWidth, img.naturalHeight, hue, matizParcial(src));
        resolve(c instanceof HTMLCanvasElement ? c.toDataURL() : src);
      };
      img.onerror = () => resolve(src);
      img.src = src;
    }));
    icones.set(key, p);
  }
  return p;
}
